import { useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Page } from '@/components/ui/page';
import { useDockState } from '@/views/dock/dock-state';
import { useRequiredParams } from 'utils';
import { Toast } from 'widgets';

import { useGlobalAccount } from '../household-queries';
import { HouseholdNoteForm, HouseholdNoteFormRef } from './HouseholdNoteForm';
import { useNoteQuery, useUpdateNoteMutation } from './note-queries';

export const EditHouseholdNotePage = () => {
    const { addUpdateNoteDockItem } = useDockState();
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [searchParams] = useSearchParams();
    const noteGUID = searchParams.get('noteGUID') ?? '';
    const navigate = useNavigate();
    const formRef = useRef<HouseholdNoteFormRef>(null);

    const globalAccountQuery = useGlobalAccount(globalAccountGUID);
    const noteQuery = useNoteQuery(noteGUID);
    const updateNoteMutation = useUpdateNoteMutation(globalAccountGUID, noteGUID);

    const defaultValues = noteQuery.data
        ? {
              subject: noteQuery.data.subject ?? '',
              content: noteQuery.data.content ?? '',
          }
        : undefined;

    return (
        <Page>
            <main className="flex flex-col gap-4 overflow-y-auto p-6 pb-8">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Note details</CardTitle>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={(e) => {
                                e.preventDefault();
                                if (formRef.current) {
                                    const formValues = formRef.current.formMethods.getValues();
                                    addUpdateNoteDockItem(noteGUID, globalAccountGUID, formValues);
                                }
                            }}
                        >
                            Pin
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {globalAccountQuery.data && noteQuery.data && (
                            <HouseholdNoteForm
                                ref={formRef}
                                showHouseholdInput={false}
                                defaultValues={defaultValues}
                                onSubmit={(data) => {
                                    updateNoteMutation
                                        .mutateAsync({ ...data, noteGUID })
                                        .then(() => {
                                            Toast.success('Note updated successfully');
                                            navigate(`/household/${globalAccountGUID}/notes`);
                                        })
                                        .catch((err) => {
                                            Toast.error(
                                                `Failed to update note: ${err?.response?.data?.message ?? err?.message ?? 'Unknown error'}`,
                                                err,
                                            );
                                        });
                                }}
                                onCancel={() => navigate(`/household/${globalAccountGUID}/notes`)}
                            />
                        )}
                    </CardContent>
                </Card>
            </main>
        </Page>
    );
};
